import { Fragment, useState, useRef, ChangeEvent } from 'react'
import { find, isEqual, castArray, uniqBy } from 'lodash-es'
import { Transition, Label } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'

import { Icon, FlexRow, Help, Loader, Form, Text } from '@/ui'
import { useOnOutsideClick, useLocaleFormattedDate } from '@/hooks'
import useModalStore from '@/stores/useModalStore'
import {
  FiltersDropdown,
  DropdownTrigger,
  Filters,
  FilterHeader,
  Filter,
  SelectedFilters,
  LoaderWrapper,
  SearchFallback,
  FluidSearch,
  FilterMode,
  SortableForm,
  SortDropdown,
  FilterLabel,
} from './Styles'
import IntervalInput from './IntervalInput'
import SpecificValueInput from './SpecificValueInput'
import useSelectableFilters from './useSelectableFilters'
import { TableColumn } from '@/types/Table'

interface TableHeaderFilterProps {
  filters?: any[]
  addFilter: (field: string, value: any) => void
  toggleFilter: (field: string) => void
  invertFilter: (filterKey: string, filter: any) => void
  toggleFilterMode: (filterKey: string, checked: boolean) => void
  removeFilter: (field: string, value: any) => void
  title?: string
  activeFilter?: string | null
  sort: (field: string, direction: string) => void
  sortDirection?: string
  sortKey?: string
  isRightColumn?: boolean
  c: TableColumn
}

const TableHeaderFilter = ({
  filters,
  addFilter,
  toggleFilter,
  invertFilter,
  toggleFilterMode,
  removeFilter,
  title,
  activeFilter,
  sort,
  sortDirection,
  sortKey,
  isRightColumn,
  c,
}: TableHeaderFilterProps) => {
  const { t } = useTranslation()
  const filterKey = c?.filters?.filterKey || c.key

  const localeFormattedDate = useLocaleFormattedDate()

  const filterDropdownRef = useRef(null)

  const [search, setSearch] = useState('')

  const { selectableFilters, loading, loadFilters } = useSelectableFilters({
    columnsFilters: c.filters,
    selectedFilters: filters,
  })

  const allFilters = find(filters, x => x.field === filterKey) || {
    queries: [],
  }

  const removeAllFilters = () => {
    allFilters.queries.forEach((filter: any) => {
      removeFilter(filterKey, filter)
    })
  }

  // Toggle dropdown if visible when clicking outside
  useOnOutsideClick(filterDropdownRef, () => {
    if (activeFilter === filterKey) {
      toggleFilter(filterKey)
    }
  })

  const filter = filters?.find(x => x.field === filterKey)

  const availableFilters = castArray(
    filter
      ? selectableFilters.filter(x => !filter.queries.includes(x.value))
      : selectableFilters,
  ).filter(({ text, render, node }) =>
    !!render && c.filters?.useRenderedValueAsSearchValue
      ? (
          render(node, t)?.toLowerCase() ?? t('No value').toLowerCase()
        ).includes(search.toLowerCase())
      : (text?.toLowerCase() ?? t('No value').toLowerCase()).includes(
          search.toLowerCase(),
        ),
  )

  const handleToggleFilter = () => {
    toggleFilter(filterKey)
    loadFilters()
  }

  const isSorted =
    sortKey === (c.sortKey || filterKey) ||
    (Array.isArray(sortKey) && isEqual(sortKey, c.sortKey))

  const showIntervalFilter =
    c.filters?.addIntervalFilter &&
    t('Add interval filter').toLowerCase().includes(search.toLowerCase())

  const showTextSearchFilter = c.filters?.addSearchFilter

  return (
    <div ref={filterDropdownRef} title={title}>
      <FiltersDropdown>
        <DropdownTrigger onClick={handleToggleFilter}>
          {c.help ? (
            <FlexRow noMargin>
              <div>
                <Icon button name="filter" />
                <Text.Span light>{title}</Text.Span>
              </div>
              <div>
                <Help content={t(c.help)} />
              </div>
            </FlexRow>
          ) : (
            <Fragment>
              <Icon button name="filter" />
              <Text.Span light overflowEllipsis>
                {title}
              </Text.Span>
            </Fragment>
          )}
        </DropdownTrigger>

        <Transition
          visible={activeFilter === filterKey}
          animation="slide down"
          duration={100}>
          <Filters isRightColumn={isRightColumn}>
            {loading ? (
              <Filter>
                <LoaderWrapper>
                  <Loader active color="gray" size="small">
                    {t('Loading filters')}
                  </Loader>
                </LoaderWrapper>
              </Filter>
            ) : (
              <Fragment>
                {c.sortable && (
                  <SortableForm>
                    <FlexRow
                      noMargin
                      justifyContent="flex-start"
                      alignItems="center">
                      <div>{t('Sort')}:</div>
                      <SortDropdown
                        value={isSorted ? sortDirection : null}
                        onChange={(e: any, { value }: any) =>
                          sort(c.sortKey || filterKey, value)
                        }
                        placeholder={t('Sort column')}
                        options={[
                          {
                            key: 'asc',
                            value: 'asc',
                            text: t('Ascending'),
                          },
                          {
                            key: 'desc',
                            value: 'desc',
                            text: t('Descending'),
                          },
                        ]}
                      />
                    </FlexRow>
                  </SortableForm>
                )}

                {/* 
                  No need to show the search bar if the filter is an interval filter, 
                  since there will never be more than two options; "No value" and "Add interval filter".

                  Also need to make sure the search bar always unmounts/mounts when the filter is toggled,
                  so the autoFocus works properly 
                */}
                {!showIntervalFilter && activeFilter === filterKey && (
                  <FluidSearch
                    autoFocus
                    value={search}
                    showNoResults={false}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        if (!showTextSearchFilter) {
                          return
                        }

                        e.preventDefault()
                        addFilter(
                          filterKey,
                          (e.target as HTMLInputElement)?.value,
                        )
                        setSearch('')
                      }
                    }}
                    onSearchChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSearch(e?.target?.value)
                    }
                  />
                )}

                {!!availableFilters.length && (
                  <FilterHeader>{t('Available filters')}</FilterHeader>
                )}

                {!showTextSearchFilter &&
                  !!search.length &&
                  !availableFilters.length &&
                  !showIntervalFilter && (
                    <SearchFallback>
                      {t('Your search yielded no results')}
                    </SearchFallback>
                  )}

                {!!search.length && showTextSearchFilter && (
                  <Filter
                    onClick={() => {
                      addFilter(filterKey, search)
                      setSearch('')
                    }}>
                    {t('Search for "{{keyword}}"', {
                      keyword: search,
                    })}
                  </Filter>
                )}

                {uniqBy(availableFilters, 'value').map((filter, i) => {
                  if (filter.render && filter.render(filter.node, t) == null) {
                    return null
                  }

                  return (
                    <Filter
                      key={i}
                      onClick={() => addFilter(filterKey, filter.value)}>
                      {filter.render
                        ? filter.render(filter.node, t)
                        : filter.text || t('No value')}
                    </Filter>
                  )
                })}

                {showIntervalFilter && (
                  <Filter
                    onClick={() =>
                      useModalStore.getState().openModal({
                        header: t('Add interval filter'),
                        body: (
                          <IntervalInput
                            type={c.type}
                            onSubmit={(values: any) => {
                              addFilter(filterKey, values)
                              useModalStore.getState().closeModal()
                            }}
                            metadata={c.metadata}
                          />
                        ),
                        size: 'tiny',
                        closeConfirm: false,
                      })
                    }>
                    {t('Add interval filter')}
                  </Filter>
                )}

                {c.type === 'number' && c.key.includes('formField') && (
                  <Filter
                    onClick={() =>
                      useModalStore.getState().openModal({
                        header: t('Filter on specific value'),
                        body: (
                          <SpecificValueInput
                            type={c.type}
                            onSubmit={value => {
                              addFilter(filterKey, value)
                              useModalStore.getState().closeModal()
                            }}
                            metadata={c.metadata}
                          />
                        ),
                        size: 'tiny',
                        closeConfirm: false,
                      })
                    }>
                    {t('Filter on specific value')}
                  </Filter>
                )}
              </Fragment>
            )}
          </Filters>
        </Transition>

        {isSorted && <Icon name={`sort ${sortDirection}ending`} />}
      </FiltersDropdown>

      <SelectedFilters>
        {c.isArray && allFilters.queries.length > 1 && (
          <FilterMode>
            <Form.Checkbox
              toggle
              defaultChecked={allFilters.matchAll}
              label={t('AND filter')}
              onChange={(e, { checked }) =>
                toggleFilterMode(filterKey, !!checked)
              }
            />
            <Help
              position="top center"
              content={t('Entries must match all selected filters')}
            />
          </FilterMode>
        )}

        {allFilters.queries.length > 1 && (
          <Label
            style={{ marginBottom: '.25em' }}
            as="a"
            color="orange"
            onClick={() => removeAllFilters()}
            size="small">
            {t('Remove all filters')}
          </Label>
        )}

        {allFilters.queries.map((filter: any) => {
          const negativeFilters = (
            find(filters, x => x.field === filterKey) || {
              negativeQueries: [],
            }
          ).negativeQueries

          const { text, translateResult, render } =
            selectableFilters.find(x => x.value === filter) || {}

          let displayedFilterText

          if (render) {
            displayedFilterText = render(
              selectableFilters.find(
                availableFilter => availableFilter.value === filter,
              ).node,
              t,
            )
          } else if (text) {
            displayedFilterText =
              (translateResult ? t(text) : text) || t('No value')
          } else if (typeof filter === 'string' || typeof filter === 'number') {
            displayedFilterText = filter
          }

          const renderIntervalFilterText = (
            min: Date | number,
            max: Date | number,
          ) => {
            if (min && !max) {
              return c.type === 'date'
                ? t('On or after {{date}}', {
                    date: localeFormattedDate(min as Date),
                  })
                : t('At or above {{value}}', { value: min })
            } else if (!min && max) {
              return c.type === 'date'
                ? t('On or before {{date}}', {
                    date: localeFormattedDate(max as Date),
                  })
                : t('At or below {{value}}', { value: max })
            }

            return t('Between {{min}} and {{max}}', {
              min:
                c.type === 'date'
                  ? localeFormattedDate(filter.min)
                  : filter.min,
              max:
                c.type === 'date'
                  ? localeFormattedDate(filter.max)
                  : filter.max,
            })
          }

          const filterName =
            filter?.type === 'interval'
              ? renderIntervalFilterText(filter.min, filter.max)
              : displayedFilterText ?? t('No value')

          return (
            <div
              key={filter}
              style={{ maxWidth: '100%', objectFit: 'contain' }}>
              <FilterLabel
                negative={negativeFilters.includes(filter)}
                onClick={() => invertFilter?.(filterKey, filter)}>
                <div title={t('Click to invert filter')}>
                  <Text.Span overflowEllipsis>{filterName}</Text.Span>
                </div>
                <div
                  className="icon-wrapper"
                  title={t('Remove filter: {{name}}', { name: filterName })}
                  onClick={e => {
                    e.stopPropagation()
                    removeFilter(filterKey, filter)
                  }}>
                  <Icon button name="icon-trash" color="white" size="small" />
                </div>
              </FilterLabel>
            </div>
          )
        })}
      </SelectedFilters>
    </div>
  )
}

export default TableHeaderFilter
